import React, { useMemo, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import {
  Divider,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  CheckBoxField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import Close from '@mui/icons-material/Close';
import { Add, Remove } from '@mui/icons-material';

import { useCompany } from '@dispatch/shared/hooks';
import {
  MultiSelectOption,
  GroupAttributeMatchMode,
} from '@dispatch/shared/types';
import { GroupAttributeFormData } from './modalContent';

interface SelectAttributeValuesVarcharsForm {
  integrationGUID?: string;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  title: string;
  value: string | undefined;
  displayName?: string;
}

const emptyAttributeValueVarCharForm = (
  selectedAttributeDefinitionIntegrationGUID: string
): SelectAttributeValuesVarcharsForm => {
  return {
    selectedAttributeDefinitionIntegrationGUID:
      selectedAttributeDefinitionIntegrationGUID,
    attributeDefinitionValueIntegrationGUID: '',
    title: '',
    value: '',
  };
};

export type CompanyAttributesFormProps = {
  control: Control<FieldValues>;
  //permissions: Ability<[Actions, Subjects]> | undefined;
  index: number;
  removeAttribute: UseFieldArrayRemove;
  watch: UseFormWatch<GroupAttributeFormData>;
  field: Record<'id', string>;
  errors: FieldErrors;
  getValues: UseFormGetValues<GroupAttributeFormData>;
  setValue: UseFormSetValue<GroupAttributeFormData>;
};

export function CompanyAttributesForm({
  control,
  //permissions,
  index,
  removeAttribute,
  watch,
  field,
  errors,
  getValues,
  setValue,
}: CompanyAttributesFormProps) {
  const { company } = useCompany();
  const {
    fields: attributeValueFields,
    remove: attributeValueRemove,
    append: attributeValueAppend,
  } = useFieldArray({
    control: control,
    name: `attributes.${index}.selectedAttributeValuesVarchars`,
    keyName: 'fieldId',
  });

  const companyAttribute = company?.attributeDefinition.find(
    (attributeDef) =>
      watch(`attributes.${index}`).attributeDefinitionIntegrationGUID ===
      attributeDef.integrationGUID
  );

  const deleteAttribute = (index: number) => {
    removeAttribute(index);
    setValue('addSelectedAttributeGUID', []);
  };

  const isSelectedVarChar =
    companyAttribute?.attributeDataType?.includes('string');

  const addAttributeValue = () => {
    attributeValueAppend(
      emptyAttributeValueVarCharForm(companyAttribute?.integrationGUID || '')
    );
  };

  //to avoid re-renders as array changes length and causes re-render
  const selectedAttributeValuesVarchars = watch(
    `attributes.${index}.selectedAttributeValuesVarchars`
  );

  const disableAdd = useMemo(() => {
    return (
      selectedAttributeValuesVarchars?.length ===
      companyAttribute?.attributeDefinitionValues?.length
    );
  }, [
    companyAttribute?.attributeDefinitionValues?.length,
    selectedAttributeValuesVarchars?.length,
  ]);

  // Reset matchMode to ANY when value count drops to 1 — a single-value attribute
  // cannot have a meaningful ANY/ALL distinction, so we normalise it here.
  useEffect(() => {
    if ((selectedAttributeValuesVarchars?.length ?? 0) <= 1) {
      setValue(
        `attributes.${index}.matchMode` as any,
        GroupAttributeMatchMode.ANY
      );
    }
  }, [selectedAttributeValuesVarchars?.length, index, setValue]);

  const options = useMemo(() => {
    const selectedAttributeValuesVarchars = watch(
      `attributes.${index}.selectedAttributeValuesVarchars`
    );

    const filteredAttributes =
      companyAttribute?.attributeDefinitionValues?.filter((companyAttr) => {
        return !selectedAttributeValuesVarchars?.find(
          (selecAttr) =>
            selecAttr.attributeDefinitionValueIntegrationGUID ===
            companyAttr.integrationGUID
        );
      });

    return (
      filteredAttributes?.map((value) => ({
        title: value.displayName,
        value: value.integrationGUID,
      })) || []
    );
  }, [
    companyAttribute?.attributeDefinitionValues,
    watch(`attributes.${index}.selectedAttributeValuesVarchars`)?.map(
      (x) => x?.title
    ),
  ]);

  const deleteAttributeValue = (index: number) => {
    attributeValueRemove(index);
  };

  const onValueChange = (
    value: MultiSelectOption | (string | number)[] | null,
    valueIndex: number
  ) => {
    const option = value as MultiSelectOption;

    setValue(
      `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}`,
      {
        selectedAttributeDefinitionIntegrationGUID:
          companyAttribute?.integrationGUID || '',
        attributeDefinitionValueIntegrationGUID: option.value as string,
        title: option.title,
        value: option.value as string,
      }
    );
  };

  const childFieldError =
    errors?.['attributes']?.[index]?.selectedAttributeValuesVarchars?.message;

  const renderAttributes = () => {
    return (
      <>
        <div
          style={{
            padding: '24px',
          }}
        >
          <Grid container spacing={4} xs={12} key={field.id}>
            <Grid item xs={6} data-testid="attribute-name">
              <TextField
                id={`displayName`}
                name={`attributes.${index}.displayName`}
                label="Attribute"
                type="text"
                control={control}
                inputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => deleteAttribute(index)}>
                      <Close />
                    </IconButton>
                  ),
                }}
                errors={errors}
                error={childFieldError}
              />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <IconButton
                onClick={() => addAttributeValue()}
                disabled={disableAdd}
              >
                <Add />
              </IconButton>
              {selectedAttributeValuesVarchars?.length > 1 && (
                <Controller
                  control={control}
                  name={`attributes.${index}.matchMode`}
                  defaultValue={GroupAttributeMatchMode.ANY}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      value={field.value ?? GroupAttributeMatchMode.ANY}
                      onChange={(_, selectedMode: string | null) => {
                        if (selectedMode !== null) {
                          field.onChange(selectedMode);
                        }
                      }}
                      size="small"
                    >
                      <ToggleButton value={GroupAttributeMatchMode.ANY}>
                        <Typography variant="caption">ANY</Typography>
                      </ToggleButton>
                      <ToggleButton value={GroupAttributeMatchMode.ALL}>
                        <Typography variant="caption">ALL</Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              )}
            </Grid>
            <Grid container item xs={12} data-testid="attribute-form-varchar">
              {isSelectedVarChar &&
                attributeValueFields.map((attributeValueField, valueIndex) => {
                  return (
                    <div
                      key={`${attributeValueField.fieldId}-${valueIndex}`}
                      style={{ width: '100%' }}
                    >
                      {renderAttribute(attributeValueField, valueIndex)}
                      {/* {renderSubAttribute(valueIndex)} */}
                    </div>
                  );
                })}
            </Grid>
            {!isSelectedVarChar && (
              <Grid container item xs={6} data-testid="attribute-form-boolean">
                <CheckBoxField
                  id={`selectedAttributeValuesBools.value`}
                  name={`attributes.${index}.selectedAttributeValuesBools.value`}
                  label="Active"
                  //disabled={!hasUpsertPermissions}
                  control={control}
                />
              </Grid>
            )}
          </Grid>
        </div>
        <Divider />
      </>
    );
  };

  const renderAttribute = (
    attributeValueField: Record<'fieldId', string>,
    valueIndex: number
  ) => {
    return (
      <Grid
        container
        item
        xs={12}
        spacing={2}
        key={attributeValueField.fieldId}
        paddingTop={3}
      >
        <Grid item xs={6}>
          <SelectField
            id={`value`}
            name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}`}
            label={companyAttribute?.displayName}
            control={control}
            //readOnly={!hasUpsertPermissions}
            options={options}
            errors={
              errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                ? errors[`selectedAttributeValuesVarchars`][valueIndex]
                : null
            }
            onValueChange={(
              value: MultiSelectOption | (string | number)[] | null
            ) => onValueChange(value, valueIndex)}
          />
        </Grid>
        <Grid item xs={3}>
          <IconButton
            data-testid="remove-attribute-value"
            onClick={() => deleteAttributeValue(valueIndex)}
          >
            <Remove />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  return <Section>{renderAttributes()}</Section>;
}
